import { AaaGameConceptData, IdXboxGameConceptData, SubFormsDataTypes } from "../models/forms";
import { Programs } from "../models/programsConfig";
import {
  AaaGameInformationFormId,
  AaaGameInformationSubFormsIdsOrder,
} from "../subForms/aaaGameInformation/AaaGameInformationPageConstants";
import {
  GameInformationFormId,
  GameInformationSubFormsIdsOrder,
} from "../subForms/idXboxGameInformation/GameInformationPageConstants";
import { FormDataValidation, validateFormUxOnly } from "./formValidation";

export type GameConceptProgress = Readonly<{
  completedSections: number;
  totalSections: number;
  percentage: number;
}>;

export const calculateGameConceptProgress = (
  subFormIds: ReadonlyArray<string>,
  startedSections: ReadonlySet<string>,
  sectionValidity: Readonly<Record<string, boolean>>
): GameConceptProgress => {
  const completedSections = subFormIds.filter(
    subFormId => startedSections.has(subFormId) && sectionValidity[subFormId] === true
  ).length;
  const totalSections = subFormIds.length;

  return {
    completedSections,
    totalSections,
    percentage: totalSections === 0 ? 0 : Math.round((completedSections / totalSections) * 100),
  };
};

const getSubFormData = (
  gameConcept: AaaGameConceptData | IdXboxGameConceptData,
  subFormId: string
): SubFormsDataTypes | undefined => {
  const value: unknown = Reflect.get(gameConcept, subFormId);
  if (typeof value !== "object" || value === null || !("isStarted" in value)) {
    return undefined;
  }

  return value as SubFormsDataTypes;
};

export const calculateSavedGameConceptProgress = async (
  program: Programs,
  gameConcept: AaaGameConceptData | IdXboxGameConceptData
): Promise<GameConceptProgress> => {
  const config: Readonly<{ formId: string; subFormIds: ReadonlyArray<string> }> =
    program === Programs.Aaa
      ? {
          formId: AaaGameInformationFormId,
          subFormIds: AaaGameInformationSubFormsIdsOrder,
        }
      : {
          formId: GameInformationFormId,
          subFormIds: GameInformationSubFormsIdsOrder,
        };

  const startedSections = new Set<string>();
  const sectionValidity: Record<string, boolean> = {};

  await Promise.all(
    config.subFormIds.map(async subFormId => {
      const subFormData = getSubFormData(gameConcept, subFormId);
      if (!subFormData) {
        sectionValidity[subFormId] = false;
        return;
      }

      if (subFormData.isStarted) {
        startedSections.add(subFormId);
      }

      try {
        sectionValidity[subFormId] =
          (await validateFormUxOnly(subFormData, config.formId, subFormId)) ===
          FormDataValidation.Valid;
      } catch {
        sectionValidity[subFormId] = false;
      }
    })
  );

  return calculateGameConceptProgress(config.subFormIds, startedSections, sectionValidity);
};
